// ContactTable.tsx
import React, { useRef } from "react";
import s from "./Sent.module.scss";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { ColumnType } from "antd/es/table";
import clsx from "clsx";
import ReusablePopover from "../../../../../../../../global components/Popover/ReuseablePopover";
import PopOverItems from "./PopOverItems/PopOverItems";
import SearchInput from "../../../../../../../../global components/Forms/SearchInput/SearchInput";
import SelectField from "../../../../../../../../global components/Forms/SelectField/SelectField";
import Paginate from "../../../../../../../../global components/Paginate/Pagination";
import ReusableTable from "../../../../../../../../global components/Table/Table";
import Unauthorised from "../../../../../../../../global components/Unauthorised";
import NoData from "../../../../../../../../global components/Table/NoData/NoData";
import Envelope from "../../../../../../../../global components/Icons/Envelope/Envelope";
import Elipses from "../../../../../../../../global components/Icons/Elipses";
import { PaginatedApplicationMessage } from "../../../../../../../../libs/Utils/Types/User";
import { parseClientError } from "../../../../../../../../libs/api/Endpoints";
import { capitalize } from "../../../../../../../../libs/common";
dayjs.extend(utc);
dayjs.extend(timezone);
interface DataType {
  key: string;
  serialNumber: number;

  messageTitle: string;
  audience: string;

  date: string;
  status: string;
  actions: string;
}
type PropType = {
  mutate: () => void;
  sentMessages: PaginatedApplicationMessage;
  isLoading: boolean;
  error: any;
  handleFilter: (value: string) => void;
  handleAudience: (value: string) => void;
  handlePageItems: (value: number) => void;
  handleCurrentPage: (value: number) => void;
  handleSearch: (value: string) => void;
  pageItems: number;
  msgType: "sent" | "scheduled" | "draft";
};
function Sent({
  mutate,
  sentMessages,
  isLoading,
  error,
  handleAudience: audienceStatus,
  handleCurrentPage,
  handleFilter: filterStatus,
  handlePageItems,
  handleSearch: search,
  pageItems,
  msgType,
}: PropType) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleSearch = (value: string) => {
    // You can add your search logic here, e.g., fetching data based on the search term
    search(value);
  };
  const handlePageChange = (page: number, size: number) => {
    handleCurrentPage(page);
    handlePageItems(size);
  };

  // Define table columns

  const getColumns = (): ColumnType<DataType>[] => {
    const columns: ColumnType<DataType>[] = [
      {
        title: "S/N",
        dataIndex: "serialNumber",
        key: "serialNumber",
        render: (serialNumber: number) => (
          <span className={clsx(s.cell, s.messageTitle)}>{serialNumber}</span>
        ),
      },
      {
        title: "Message title",
        dataIndex: "messageTitle",
        key: "messageTitle",
        render: (messageTitle: string, record: { key: string }) => (
          <span className={clsx(s.cell, s.messageTitle)}>{messageTitle}</span>
        ),
      },
      {
        title: "Recipient",
        dataIndex: "audience",
        key: "audience",
        render: (audience: string) => {
          return <span className={clsx(s.cell)}>{audience}</span>;
        },
      },

      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        render: (date: string) => (
          <span className={s.cell}>
            {" "}
            {dayjs
              .utc(date)
              .tz(dayjs.tz.guess())
              .format("MMM D, YYYY h:mm A")}{" "}
          </span>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: string) => (
          <span className={clsx(s.statusCell, s[status.toLowerCase()])}>
            {status}
          </span>
        ),
      },

      {
        title: "Actions",
        dataIndex: "actions",
        key: "actions",
        render: (text: any, record: { key: string }) => (
          <ReusablePopover
            trigger={"click"}
            placement="rightTop"
            content={
              <div className={s.multiplePopover}>
                <PopOverItems
                  mutate={mutate}
                  type={"record.mesageType"}
                  keyValue={record.key}
                  msgType={msgType}
                />
              </div>
            } // Pass the key value
          >
            <div
              className="actions-column"
              onClick={(e) => {
                e.stopPropagation(); // Prevent event from bubbling up
              }}
            >
              <Elipses height={24} width={24} cursor={"pointer"} />
            </div>
          </ReusablePopover>
        ),
      },
    ];

    return columns;
  };
  // Transform API response to table data
  const transformedData: DataType[] = sentMessages?.data.map(
    (message, index) => ({
      key: message.id || "N/A",
      serialNumber: index + 1,
      messageTitle: message.title || "N/A",
      audience:
        message.audience === "verified"
          ? "Verified users"
          : message.audience === "unverified"
          ? "Unverified users"
          : message.audience === "pending"
          ? "Pending users"
          : "All users",
      date: message.sentAt || "N/A",
      status: message.status,
      actions: "", // You can customize actions here as needed
    })
  );

  function handleFilter(value: string) {
    filterStatus(value);
  }
  function handleAudience(value: string) {
    audienceStatus(value);
  }
  return (
    <div className={s.table}>
      <span className={s.searchParams}>
        <div className={s.filters}>
          <SearchInput
            placeholder="search by title"
            handleSearch={handleSearch}
            width={332}
            height={45}
            className={s.input}
            rootClassName={s.input}
          />
          <SelectField
            label={"Status:"}
            wrapperClassname={s.SelectWrapper}
            bordered
            onChange={(value: any) => handleFilter(value)}
            defaultValue={""}
            className={s.selectInput}
            style={{ width: "100%", height: "100%" }}
            options={[
              { value: "", label: "All" }, // Option to reset the filter
              { value: "sent", label: "Sent" },
              { value: "pending", label: "Pending" },
              { value: "failed", label: "Failed" },
            ]}
          />
          <SelectField
            label={"Audience:"}
            wrapperClassname={s.SelectWrapper}
            bordered
            onChange={(value: any) => handleAudience(value)}
            defaultValue={""}
            className={s.selectInput}
            style={{ width: "100%", height: "100%" }}
            options={[
              { value: "", label: "All" }, // Option to reset the filter
              { value: "verified", label: "Verified user" },
              { value: "unverified", label: "Unverified user" },
              { value: "pending", label: "Pending user" },
            ]}
          />
        </div>
        <div className={s.paginate}>
          <Paginate
            onChange={handlePageChange}
            total={sentMessages?.pagination.totalItems || 0}
            pageSize={pageItems}
          />
        </div>
      </span>

      <div ref={containerRef} className={s.rTable}>
        <ReusableTable
          loading={isLoading}
          noDataComponent={
            error ? (
              <Unauthorised
                error={{
                  title: "Error",
                  message:
                    capitalize(parseClientError(error).message) ||
                    "Something went wrong",
                }}
              />
            ) : (
              <NoData
                Message="No message found"
                subMessage="Get started by sending a message to a contact group"
                icon={<Envelope fill="#F0EFEE" height={32} width={32} />}
              />
            )
          }
          dataSource={transformedData || []}
          columns={getColumns()} // Filter out `false` columns
        />
      </div>
    </div>
  );
}

export default Sent;
